/** @jsxImportSource theme-ui */
import { jsx } from "theme-ui";
import React from "react";
import { graphql, navigate } from "gatsby";
import { Box, Flex, Heading, Text } from "@theme-ui/components";
import { Layout, SEO } from "../../components";
import { Container } from "../../components/organisms";
import { PageHeading } from "../../components/wiki";
import HolidayList from "../../components/wiki/HolidayList";
import { Button, CustomLink } from "../../components/atoms";
import { colors, typography } from "../../styles/theme";
import { ActionBanner } from "../../components/molecules";

const HolidayPage = ({
  data: { holidayList, state, states, attachments, stateHolidayList },
  pageContext: { year },
  location: { href, search },
}) => {
  const { name, slug, cities } = state;
  const { header } = stateHolidayList;

  const crumbs = [
    {
      crumbLabel: `Holidays List ${year}`,
      path: `/holidays-list-${year}/`,
    },
    {
      crumbLabel: "All States",
      path: `/wiki/states/`,
    },
  ];

  crumbs.push({
    crumbLabel: state.name,
    path: `/wiki/acts/st-${slug}/`,
  });

  const title = `${name}<br>Public Holidays List ${year}`;
  const pageName="State_Holiday_List_Page"
	const demoBlockId = "/demo/?gc_source=state-holiday-list&ac_form_tag=AF_DEMO_STATE_HOLIDAY_LIST";

  return (
    <Layout demoBlockId={demoBlockId}>
      <PageHeading title={title} styles={headingStyles} crumbs={crumbs} />
      <Container sx={{ mb: ["4rem", "4rem", "7rem", "7rem"] }}>
        <Flex
          sx={{
            mt: "3rem",
            flexWrap: "wrap",
            justifyContent: "center",
            flexBasis: 0,
            gap: "3rem",
          }}
        >
          <Box
            sx={{
              width: ["100%", "100%", "100%", "63%"],
            }}
          >
            {header && (
              <>
                <Box sx={{ ...typography.textMd500, mt: 0 }}>
                  <div
                    sx={{
                      "& p": {
                        margin: 0,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: header }}
                  />
                </Box>
                <Box
                  sx={{
                    height: "1px",
                    width: "100%",
                    backgroundColor: colors.neutral200,
                    my: "3rem",
                  }}
                />
              </>
            )}
            {holidayList ? (
              <HolidayList
                items={holidayList.items}
                attachments={attachments}
              />
            ) : (
              <Box>
                Sorry! The holiday list for this state is not yet ready!
              </Box>
            )}
          </Box>
          <Box sx={{ width: ["17rem", "17rem", "17rem", "32%"] }}>
            <Heading as="h6" sx={{ ...typography.h6, mb: "1rem" }}>
              Holiday Lists For
            </Heading>
            <Box as="ul" sx={holidayListStyles.ul}>
              {states.nodes.map((state, index) => (
                <Box as="li" key={state.id} sx={holidayListStyles.li}>
                  <CustomLink
                    url={`/wiki/acts/st-${state.slug}/holidays/${year}`}
                  >
                    {state.name}
                  </CustomLink>
                </Box>
              ))}
              <Box as="li" sx={{ ...holidayListStyles.li, borderBottom: 0 }}>
                <Text sx={{ ...typography.textMd500 }}>
                  {`This holiday list is applicable for the following cities:`}
                  <br />
                  {cities}
                </Text>
              </Box>
            </Box>
          </Box>
        </Flex>
      </Container>
      <ActionBanner
        variant="BodyBannerV3"
        theme="primary"
        title="Discuss business needs, find out pricing or get a demo. We would love to talk."
        customStyles={{ mx: ["1.063rem", "1.063rem", "2rem", "3.125rem"] }}
      >
        <Button
          variant="outlined"
          size="large"
          id={`${pageName?.toLowerCase()}_request_demo_in_btn`}
          onClick={() =>
            navigate("/demo?gc_source=state-holiday-list&ac_form_tag=AF_DEMO_STATE_HOLIDAY_LIST")
          }
        >
          Request a Demo
        </Button>
      </ActionBanner>
    </Layout>
  );
};

const holidayListStyles = {
  ul: {
    listStyle: "none",
    padding: 0,
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "20px",
  },
  li: {
    borderBottom: `1px solid ${colors.neutral200}`,
    padding: "1rem",
  },
};

const headingStyles = {
  backgroundSize: "150px",
  backgroundPosition: "right 25px center",
};

export const holidayListData = graphql`
  query (
    $objectId: String!
    $regex: String!
    $attachmentKey: String!
    $yearRegex: String!
  ) {
    holidayList: stateHolidaysCsv(
      parent: { internal: { description: { regex: $regex } } }
    ) {
      items {
        Date
        Day
        Holiday
        HolidayType
      }
      parent {
        ... on File {
          name
        }
      }
    }
    state: datoCmsState(id: { eq: $objectId }) {
      id
      slug
      name
      cities
    }
    states: allDatoCmsState(
      filter: { holidayListYear: { regex: $yearRegex }, id: { ne: $objectId } }
      sort: { name: ASC }
    ) {
      nodes {
        id
        name
        slug
      }
    }
    attachments: allDatoCmsStateAttachment(
      filter: {
        attachmentType: { eq: $attachmentKey }
        state: { id: { eq: $objectId } }
      }
    ) {
      nodes {
        id
        name
        attachmentType
        state {
          id
          name
        }
        attachmentUrl
        attachmentFileName
        attachment {
          filename
          url
          mimeType
        }
      }
    }
    stateHolidayList: datoCmsStateHolidayList(
      state: { id: { eq: $objectId } }
      year: { regex: $yearRegex }
    ) {
      header
    }
  }
`;

export default HolidayPage;

export const Head = ({
  data: { state },
  location: { pathname },
  pageContext: { year },
}) => {
  const { name } = state;
  const seo_title = `${name} Holidays List for ${year}`;
  const seo_description = `Government holidays for ${year} in ${name} sourced and verified from official notifications.`;

  return (
    <SEO title={seo_title} desc={seo_description} pathname={pathname} article />
  );
};
